import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import Axios from "axios";
import { URL_LIST_ALL } from "../configs/api";
import { actions } from "../redux/authRedux";
import "../../src/assets/css/login.css";
import Spinner from "../component/Spinner";
import noibo from "../assets/img/bhs-noibo-logo.png";
import configData from "../configs/config.json";

const STATE_KEY = "hrm_sso_state";
const VERIFIER_KEY = "hrm_sso_code_verifier";
const EXCHANGE_LOCK_KEY = "hrm_sso_code_exchange_lock";

const base64UrlEncode = (value) => {
  const bytes = value instanceof ArrayBuffer ? new Uint8Array(value) : value;
  let binary = "";

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return window
    .btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
};

const randomBase64Url = (length = 64) => {
  const bytes = new Uint8Array(length);
  window.crypto.getRandomValues(bytes);

  return base64UrlEncode(bytes);
};

const createPkce = async () => {
  if (!window.crypto?.subtle) {
    throw new Error("Trình duyệt không hỗ trợ Web Crypto để tạo PKCE. Vui lòng dùng HTTPS hoặc localhost.");
  }

  const verifier = randomBase64Url(64);
  const data = new TextEncoder().encode(verifier);
  const digest = await window.crypto.subtle.digest("SHA-256", data);

  return {
    verifier,
    challenge: base64UrlEncode(digest),
  };
};

const getIssuer = () => {
  if (window.location.hostname === "localhost" && configData.SSO_ISSUER_LOCAL) {
    return configData.SSO_ISSUER_LOCAL.replace(/\/+$/g, "");
  }

  return (configData.SSO_ISSUER || "https://auths.bhsgroup.vn").replace(/\/+$/g, "");
};

const getRedirectUri = () => {
  if (configData.SSO_REDIRECT_URI) {
    return configData.SSO_REDIRECT_URI;
  }

  return `${window.location.origin}${configData.SSO_REDIRECT_PATH || "/login-sso"}`;
};

const getBackendCallbackUrl = () => {
  const endpoint = configData.SSO_BACKEND_CALLBACK_ENDPOINT || "sso/callback";

  if (/^https?:\/\//i.test(endpoint)) {
    return endpoint;
  }

  return `${URL_LIST_ALL.replace(/\/+$/g, "")}/${endpoint.replace(/^\/+/g, "")}`;
};

const createConfig = (_token) => ({
  headers: {
    Authorization: `Bearer ${_token}`,
    "Content-Type": "application/json; charset=UTF-8",
    search: "",
  },
});

const normalizeLoginResponse = (payload) => {
  if (!payload) {
    throw new Error("SSO callback không trả dữ liệu đăng nhập.");
  }

  if (payload.status && payload.status !== "success") {
    throw new Error(payload.message || "Đăng nhập SSO không thành công.");
  }

  const token = payload.token || payload.access_token;
  const loginData = payload.data || payload.user || payload.profile;

  if (!token || !loginData) {
    throw new Error("Backend HRM cần trả về token và thông tin user sau khi xác thực SSO.");
  }

  return {
    data: loginData,
    access_token: token,
    config: createConfig(token),
    expires: payload.expires || parseInt(new Date().getTime() / 1000) + 365 * 24 * 60 * 60,
  };
};

export default function LoginSSO() {
  const { isAuthorized } = useSelector(
    ({ auth }) => ({ isAuthorized: auth.user != null }),
    shallowEqual
  );
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("Đang chuyển tới Login Portal...");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isAuthorized) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const oauthError = params.get("error");

    const startSsoLogin = async () => {
      try {
        setLoading(true);
        setError("");
        setMessage("Đang tạo yêu cầu đăng nhập SSO...");

        const pkce = await createPkce();
        const nextState = randomBase64Url(32);

        sessionStorage.setItem(STATE_KEY, nextState);
        sessionStorage.setItem(VERIFIER_KEY, pkce.verifier);

        const authorizeUrl = new URL(`${getIssuer()}/oauth/authorize`);
        authorizeUrl.searchParams.set("response_type", "code");
        authorizeUrl.searchParams.set("client_id", configData.SSO_CLIENT_ID || "hrm-service");
        authorizeUrl.searchParams.set("redirect_uri", getRedirectUri());
        authorizeUrl.searchParams.set("scope", configData.SSO_SCOPES || "openid profile email offline_access");
        authorizeUrl.searchParams.set("state", nextState);
        authorizeUrl.searchParams.set("code_challenge", pkce.challenge);
        authorizeUrl.searchParams.set("code_challenge_method", "S256");

        window.location.assign(authorizeUrl.toString());
      } catch (err) {
        setLoading(false);
        setError(err.message || "Không thể bắt đầu đăng nhập SSO.");
      }
    };

    const finishSsoLogin = async () => {
      try {
        setLoading(true);
        setError("");
        setMessage("Đang xác thực SSO với HRM...");

        if (oauthError) {
          throw new Error(params.get("error_description") || oauthError);
        }

        const savedState = sessionStorage.getItem(STATE_KEY);
        const codeVerifier = sessionStorage.getItem(VERIFIER_KEY);

        if (!code || !state || !savedState || state !== savedState) {
          throw new Error("Phiên SSO không hợp lệ hoặc đã hết hạn. Vui lòng thử lại.");
        }

        if (!codeVerifier) {
          throw new Error("Không tìm thấy PKCE code verifier. Vui lòng đăng nhập lại.");
        }

        if (sessionStorage.getItem(EXCHANGE_LOCK_KEY) === code) {
          setMessage("Đang hoàn tất đăng nhập SSO...");
          return;
        }

        sessionStorage.setItem(EXCHANGE_LOCK_KEY, code);

        const res = await Axios.post(
          getBackendCallbackUrl(),
          {
            code,
            state,
            redirect_uri: getRedirectUri(),
            code_verifier: codeVerifier,
            issuer: getIssuer(),
            client_id: configData.SSO_CLIENT_ID || "hrm-service",
          }
        );

        const createUser = normalizeLoginResponse(res.data);

        sessionStorage.removeItem(STATE_KEY);
        sessionStorage.removeItem(VERIFIER_KEY);
        sessionStorage.removeItem(EXCHANGE_LOCK_KEY);
        window.history.replaceState({}, document.title, window.location.pathname);

        if (window.opener && !window.opener.closed) {
          setMessage("Đăng nhập thành công, đang quay lại HRM...");
          window.opener.postMessage(
            {
              type: "HRM_SSO_LOGIN_SUCCESS",
              payload: createUser,
            },
            window.location.origin
          );

          window.close();
          return;
        }

        dispatch(actions.login(createUser));
      } catch (err) {
        const responseMessage = err.response?.data?.message || err.response?.data?.error_description || err.response?.data?.error;
        setLoading(false);
        setError(responseMessage || err.message || "Đăng nhập SSO không thành công.");
      }
    };

    if (code || oauthError) {
      finishSsoLogin();
      return;
    }

    startSsoLogin();
  }, [dispatch, isAuthorized]);

  const retry = () => {
    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(VERIFIER_KEY);
    sessionStorage.removeItem(EXCHANGE_LOCK_KEY);
    window.location.href = "/login-sso";
  };

  if (isAuthorized) {
    return <Redirect to="/" />;
  }

  return (
    <div className="layout-login">
      <div className="container btn-login-gr">
        <div className="mb-3 w-100 d-flex justify-content-center">
          <img src={noibo} alt="logo" width={300} />
        </div>
        <div className="mt-3 w-100 d-flex justify-content-center">
          <div className="wrap-login100 p-t-50 p-b-30">
            <div className="text-center text-white">
              <h5 className="mb-3 text-white">Đăng nhập SSO</h5>
              {loading ? (
                <div className="d-flex align-items-center justify-content-center flex-column">
                  <Spinner />
                  <div className="mt-3">{message}</div>
                </div>
              ) : (
                <div>
                  <div className="alert alert-danger text-start" role="alert">
                    {error}
                  </div>
                  <button type="button" className="login100-form-btn w-100 mb-3" onClick={retry}>
                    Thử lại SSO
                  </button>
                  <button type="button" className="login100-form-btn w-100" onClick={() => { window.location.href = "/auth/login"; }}>
                    Đăng nhập thường
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
